import {
  ApiError,
  formatMoney,
  type GiftCard,
  type GiftCardKind,
  type GiftCardStatus,
  type GiftCardTransaction,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  type BadgeProps,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Copy, Gift, Plus, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { InfiniteScrollFooter } from '../components/data-table.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const STATUS_TONE: Record<GiftCardStatus, NonNullable<BadgeProps['tone']>> = {
  pending: 'warning',
  active: 'success',
  void: 'neutral',
}

const STATUS_LABEL: Record<GiftCardStatus, string> = {
  pending: 'Awaiting payment',
  active: 'Active',
  void: 'Void',
}

const KIND_LABEL: Record<GiftCardKind, string> = { digital: 'Digital', physical: 'Physical' }

const ANY = '__any__'

/** Dollars typed by a person to whole cents; null when it isn't a usable amount. */
function dollarsToCents(text: string): number | null {
  const n = Number(text)
  if (!text.trim() || !Number.isFinite(n) || n <= 0) return null
  return Math.round(n * 100)
}

export function GiftCardsPage() {
  const { can } = useAuth()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<GiftCardStatus | typeof ANY>(ANY)
  const [kind, setKind] = useState<GiftCardKind | typeof ANY>(ANY)
  const [issuing, setIssuing] = useState(false)
  const [viewingId, setViewingId] = useState<string | null>(null)
  const canManage = can('gift_cards:manage')

  const filters = {
    q: search || undefined,
    status: status === ANY ? undefined : status,
    kind: kind === ANY ? undefined : kind,
    pageSize: 50,
  }
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: queryKeys.giftCards.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<{ items: GiftCard[]; total: number; page: number; pageSize: number }>('/gift-cards', {
        query: { ...filters, page: pageParam },
      }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  })
  const items = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0
  const filtered = Boolean(search) || status !== ANY || kind !== ANY

  const heading =
    'px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted'

  return (
    <Page>
      <PageHeader
        title="Gift cards"
        description="Stored-value cards sold on the website and at the kiosk, or issued here. Customers spend them at checkout; the balance stays on the card."
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setIssuing(true)}>
              <Plus /> Issue a gift card
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-foreground-muted" />
          <Input
            className="ps-8"
            placeholder="Last 4 of the code, email, order number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as GiftCardStatus | typeof ANY)}>
          <SelectTrigger className="w-44" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Awaiting payment</SelectItem>
            <SelectItem value="void">Void</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={(v) => setKind(v as GiftCardKind | typeof ANY)}>
          <SelectTrigger className="w-40" aria-label="Filter by kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Digital or physical</SelectItem>
            <SelectItem value="digital">Digital</SelectItem>
            <SelectItem value="physical">Physical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                <th className={heading}>Card</th>
                <th className={heading}>Status</th>
                <th className={heading}>Balance</th>
                <th className={heading}>Sold on</th>
                <th className={heading}>For</th>
                <th className={heading}>Created</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : items.length > 0 ? (
                items.map((card) => (
                  <tr key={card.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2">
                      <div className="font-mono font-medium">…{card.codeLast4}</div>
                      <div className="text-xs text-foreground-muted">{KIND_LABEL[card.kind]}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={STATUS_TONE[card.status]}>{STATUS_LABEL[card.status]}</Badge>
                    </td>
                    <td className="px-3 py-2 tabular">
                      {formatMoney(card.balanceCents)}
                      <span className="text-foreground-muted">
                        {' '}
                        of {formatMoney(card.initialCents)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {card.purchaseOrderId && card.purchaseOrderNumber ? (
                        <Link
                          to="/orders/$orderId"
                          params={{ orderId: card.purchaseOrderId }}
                          className="text-primary hover:underline"
                        >
                          {card.purchaseOrderNumber}
                        </Link>
                      ) : (
                        <span className="text-foreground-muted">Issued by staff</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {card.recipientEmail ?? <span className="text-foreground-muted">—</span>}
                    </td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {new Date(card.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-end">
                      <Button variant="secondary" size="sm" onClick={() => setViewingId(card.id)}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Gift}
                      title={filtered ? 'No matching gift cards' : 'No gift cards yet'}
                      description={
                        filtered
                          ? 'Try a different search or filter.'
                          : 'Cards appear here when customers buy them, or when you issue one.'
                      }
                      variant={filtered ? 'no-results' : undefined}
                      onClearFilters={
                        filtered
                          ? () => {
                              setSearch('')
                              setStatus(ANY)
                              setKind(ANY)
                            }
                          : undefined
                      }
                      action={
                        !filtered && canManage
                          ? { label: 'Issue a gift card', onClick: () => setIssuing(true) }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <InfiniteScrollFooter
          total={total}
          loaded={items.length}
          hasMore={Boolean(hasNextPage)}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => void fetchNextPage()}
        />
      </div>

      <IssueGiftCardDialog open={issuing} onOpenChange={setIssuing} />
      <GiftCardDetailDialog
        cardId={viewingId}
        canManage={canManage}
        onClose={() => setViewingId(null)}
      />
    </Page>
  )
}

function IssueGiftCardDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [kind, setKind] = useState<GiftCardKind>('digital')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ code: string; amountCents: number } | null>(null)

  useEffect(() => {
    if (open) return
    setAmount('')
    setKind('digital')
    setEmail('')
    setName('')
    setReason('')
    setError(null)
    setIssued(null)
  }, [open])

  const issue = useMutation({
    mutationFn: async () => {
      const amountCents = dollarsToCents(amount)
      if (amountCents === null) throw new Error('Enter the amount in dollars')
      if (reason.trim().length < 3) throw new Error('Say why this card is being issued')
      return api.post<{ card: GiftCard; code: string }>('/gift-cards', {
        amountCents,
        kind,
        recipientEmail: kind === 'digital' && email.trim() ? email.trim() : null,
        recipientName: kind === 'digital' && name.trim() ? name.trim() : null,
        reason: reason.trim(),
      })
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.giftCards.all() })
      setIssued({ code: result.code, amountCents: result.card.initialCents })
    },
    onError: (e: Error) => setError(e instanceof ApiError ? e.message : e.message),
  })

  if (issued) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gift card issued</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-foreground-muted">
              This {formatMoney(issued.amountCents)} card is active now. The full code is shown
              {email.trim() ? ' here and has been emailed' : ' only here'} — write it down or copy
              it before closing.
            </p>
            <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-sunken px-4 py-3">
              <span className="font-mono text-lg font-semibold tracking-wide">{issued.code}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(issued.code)
                  toast.success('Copied')
                }}
              >
                <Copy /> Copy
              </Button>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Issue a gift card</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-foreground-muted">
            For a make-good, a promotion or a gift — no payment is taken. A card a customer paid for
            is created by their order, not here.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount" required hint="In dollars.">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="25.00"
                />
              )}
            </Field>
            <Field label="Kind" required>
              {(props) => (
                <Select value={kind} onValueChange={(v) => setKind(v as GiftCardKind)}>
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="digital">Digital</SelectItem>
                    <SelectItem value="physical">Physical</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          {kind === 'digital' ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email it to" hint="Optional. Leave blank to just show the code.">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Recipient name">
                {(props) => (
                  <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />
                )}
              </Field>
            </div>
          ) : null}
          <Field label="Reason" required hint="Kept with the card's history.">
            {(props) => (
              <Textarea
                {...props}
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={issue.isPending} onClick={() => issue.mutate()}>
            Issue card
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function GiftCardDetailDialog({
  cardId,
  canManage,
  onClose,
}: {
  cardId: string | null
  canManage: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [revealed, setRevealed] = useState<string | null>(null)
  const [voiding, setVoiding] = useState(false)
  const [voidReason, setVoidReason] = useState('')

  // Start fresh whenever a different card is opened.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `cardId` is the trigger, not a value read inside
  useEffect(() => {
    setRevealed(null)
    setVoiding(false)
    setVoidReason('')
  }, [cardId])

  const card = useQuery({
    queryKey: queryKeys.giftCards.detail(cardId ?? ''),
    queryFn: () => api.get<GiftCard>(`/gift-cards/${cardId}`),
    enabled: cardId !== null,
  })
  const history = useQuery({
    queryKey: queryKeys.giftCards.transactions(cardId ?? ''),
    queryFn: () => api.get<GiftCardTransaction[]>(`/gift-cards/${cardId}/transactions`),
    enabled: cardId !== null,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.giftCards.all() })
  const onError = (error: Error) =>
    toast.error('Could not do that', error instanceof ApiError ? error.message : undefined)

  const reveal = useMutation({
    mutationFn: () => api.post<{ code: string }>(`/gift-cards/${cardId}/reveal`),
    onSuccess: (r) => setRevealed(r.code),
    onError,
  })
  const resend = useMutation({
    mutationFn: () => api.post(`/gift-cards/${cardId}/resend-email`),
    onSuccess: async () => {
      toast.success('Email sent')
      await refresh()
    },
    onError,
  })
  const attach = useMutation({
    mutationFn: () => api.post(`/gift-cards/${cardId}/mark-attached`),
    onSuccess: async () => {
      toast.success('Marked as attached — the code is no longer kept')
      setRevealed(null)
      await refresh()
    },
    onError,
  })
  const voidCard = useMutation({
    mutationFn: () => api.post(`/gift-cards/${cardId}/void`, { reason: voidReason.trim() }),
    onSuccess: async () => {
      toast.success('Gift card voided')
      setVoiding(false)
      setVoidReason('')
      await refresh()
    },
    onError,
  })

  const c = card.data

  return (
    <Dialog open={cardId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{c ? `Gift card …${c.codeLast4}` : 'Gift card'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {c ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                <Badge>{KIND_LABEL[c.kind]}</Badge>
                <span className="tabular text-sm">
                  {formatMoney(c.balanceCents)} left of {formatMoney(c.initialCents)}
                </span>
              </div>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-foreground-muted">Sold on</dt>
                <dd>
                  {c.purchaseOrderId && c.purchaseOrderNumber ? (
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: c.purchaseOrderId }}
                      className="text-primary hover:underline"
                    >
                      Order {c.purchaseOrderNumber}
                    </Link>
                  ) : (
                    `Issued by staff${c.issuedReason ? ` — ${c.issuedReason}` : ''}`
                  )}
                </dd>
                {c.purchaserName ? (
                  <>
                    <dt className="text-foreground-muted">Bought by</dt>
                    <dd>{c.purchaserName}</dd>
                  </>
                ) : null}
                {c.recipientEmail ? (
                  <>
                    <dt className="text-foreground-muted">Sent to</dt>
                    <dd>
                      {c.recipientName ? `${c.recipientName} · ` : ''}
                      {c.recipientEmail}
                      {c.deliveredAt
                        ? ` — delivered ${new Date(c.deliveredAt).toLocaleString()}`
                        : ' — not delivered yet'}
                    </dd>
                  </>
                ) : null}
              </dl>

              {revealed ? (
                <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-sunken px-4 py-3">
                  <span className="font-mono text-lg font-semibold tracking-wide">{revealed}</span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(revealed)
                      toast.success('Copied')
                    }}
                  >
                    <Copy /> Copy
                  </Button>
                </div>
              ) : null}

              {canManage && c.status !== 'void' ? (
                <div className="flex flex-wrap gap-2">
                  {c.codeAvailable ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={reveal.isPending}
                      onClick={() => reveal.mutate()}
                    >
                      Show full code
                    </Button>
                  ) : null}
                  {c.codeAvailable && c.kind === 'digital' && c.recipientEmail ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate()}
                    >
                      Email it again
                    </Button>
                  ) : null}
                  {c.codeAvailable &&
                  c.status === 'active' &&
                  (c.kind === 'physical' || !c.recipientEmail) ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={attach.isPending}
                      onClick={() => attach.mutate()}
                    >
                      {c.kind === 'physical' ? 'Code is on the card' : 'Code handed over'}
                    </Button>
                  ) : null}
                  <Button variant="danger" size="sm" onClick={() => setVoiding(true)}>
                    Void card
                  </Button>
                </div>
              ) : null}

              {voiding ? (
                <div className="space-y-2 rounded-md border border-border p-3">
                  <Field
                    label="Why is it being voided?"
                    required
                    hint="Voiding takes the remaining balance off the card for good."
                  >
                    {(props) => (
                      <Textarea
                        {...props}
                        rows={2}
                        value={voidReason}
                        onChange={(e) => setVoidReason(e.target.value)}
                      />
                    )}
                  </Field>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setVoiding(false)}>
                      Keep it
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={voidReason.trim().length < 3 || voidCard.isPending}
                      onClick={() => voidCard.mutate()}
                    >
                      Void card
                    </Button>
                  </div>
                </div>
              ) : null}

              <div>
                <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-foreground-muted">
                  History
                </h3>
                <ul className="divide-y divide-border rounded-md border border-border text-sm">
                  {(history.data ?? []).map((t) => (
                    <li key={t.id} className="flex items-start justify-between gap-3 px-3 py-2">
                      <div>
                        <div className="capitalize">{t.kind}</div>
                        <div className="text-xs text-foreground-muted">
                          {new Date(t.createdAt).toLocaleString()}
                          {t.orderNumber ? ` · order ${t.orderNumber}` : ''}
                          {t.actorLabel ? ` · ${t.actorLabel}` : ''}
                          {t.note ? ` · ${t.note}` : ''}
                        </div>
                      </div>
                      <div className="tabular text-end">
                        <div className={t.amountCents < 0 ? 'text-danger' : 'text-success'}>
                          {t.amountCents < 0 ? '−' : '+'}
                          {formatMoney(Math.abs(t.amountCents))}
                        </div>
                        <div className="text-xs text-foreground-muted">
                          balance {formatMoney(t.balanceAfterCents)}
                        </div>
                      </div>
                    </li>
                  ))}
                  {history.data?.length === 0 ? (
                    <li className="px-3 py-2 text-foreground-muted">Nothing yet.</li>
                  ) : null}
                </ul>
              </div>
            </>
          ) : (
            <p className="text-sm text-foreground-muted">Loading…</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
